import { useCustomers } from '../hooks/useCustomers';
import { useDevices } from '../hooks/useDevices';
import { useCapacity } from '../hooks/useCapacity';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function Dashboard() {
  const { data: customers, isLoading: customersLoading } = useCustomers();
  const { data: devices, isLoading: devicesLoading } = useDevices();
  const { data: capacity, isLoading: capacityLoading } = useCapacity();

  const isLoading = customersLoading || devicesLoading || capacityLoading;

  const stats = {
    totalCustomers: customers?.length ?? 0,
    activeCustomers: customers?.filter((c) => c.status === 'active').length ?? 0,
    totalDevices: devices?.length ?? 0,
    activeDevices: devices?.filter((d) => d.status === 'active').length ?? 0,
  };

  const eip = capacity?.elastic_ips;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard title="Total Customers" value={stats.totalCustomers} />
        <StatCard title="Active Customers" value={stats.activeCustomers} color="green" />
        <StatCard title="Total Devices" value={stats.totalDevices} />
        <StatCard title="Active Devices" value={stats.activeDevices} color="green" />
      </div>

      {eip && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Elastic IP Capacity</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="EIP Limit" value={eip.limit} />
            <StatCard title="EIPs Used" value={eip.used} />
            <StatCard
              title="EIPs Available"
              value={eip.available}
              color={eip.available <= 0 ? 'red' : eip.available <= 2 ? 'yellow' : 'green'}
            />
            <StatCard title="Device IPs" value={eip.device_ips} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Customers</h2>
          {customers && customers.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {customers.slice(0, 5).map((customer) => (
                <li key={customer.customer_id} className="py-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">
                      {customer.organization_name}
                    </span>
                    <span className="text-sm text-gray-500">{customer.tier}</span>
                  </div>
                  <p className="text-sm text-gray-500">{customer.billing_email}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No customers yet</p>
          )}
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Devices</h2>
          {devices && devices.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {devices.slice(0, 5).map((device) => (
                <li key={device.device_id} className="py-3">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-900">{device.device_name}</span>
                    <span className="text-sm text-gray-500">{device.status}</span>
                  </div>
                  <p className="text-sm text-gray-500">{device.assigned_ip}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No devices yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: number;
  color?: 'default' | 'green' | 'yellow' | 'red';
}

function StatCard({ title, value, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'text-gray-900',
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
  };

  return (
    <div className="card p-6">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className={`text-3xl font-bold mt-2 ${colorClasses[color]}`}>{value}</p>
    </div>
  );
}
